import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { getWalletByUserId, getTransactionsByWalletId } from '../../services/financeService';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Container,
  Alert,
  Fade,
  Card,
  CardContent,
  TablePagination
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import BilleteraHeader from '../../components/Billetera/BilleteraHeader';
import TransactionList from '../../components/Billetera/TransactionList';
import AddCreditModal from '../../components/Billetera/AddCreditModal';
import MigrationCodeCard from '../../components/Migration/MigrationCodeCard';
import SaldoInsuficienteAlert from '../../components/Billetera/SaldoInsuficienteAlert';

const POLL_INTERVAL_MS = 3000;   // 3s entre intentos
const POLL_TOTAL_MS = 60000;     // 60s en total (puedes subirlo a 120000 si lo prefieres)

const BilleteraContent = () => {
  const { user } = useContext(AuthContext);
  const isAdminUser = user?.is_admin;

  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const pollTimerRef = useRef(null);
  const pollEndTimeRef = useRef(0);
  const isPollingRef = useRef(false);

  const clearPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    isPollingRef.current = false;
    pollEndTimeRef.current = 0;
  };

  const fetchWalletData = useCallback(async () => {
    if (!user || !user.id || isAdminUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const walletData = await getWalletByUserId(user.id);
      if (walletData) {
        walletData.balance = parseFloat(walletData.balance) || 0;
        walletData.credit_limit = parseFloat(walletData.credit_limit) || 0;
      }
      setWallet(walletData);

      if (walletData && walletData.wallet_id) {
        try {
          const transactionsData = await getTransactionsByWalletId(walletData.wallet_id);
          setTransactions(Array.isArray(transactionsData.data) ? transactionsData.data : []);
        } catch (transactionsError) {
          console.error('Error al cargar las transacciones:', transactionsError);
          setTransactions([]);
        }
      } else {
        setTransactions([]);
      }
    } catch (walletError) {
      setError('Error al cargar los datos de la billetera.');
      console.error(walletError);
    } finally {
      setLoading(false);
    }
  }, [user, isAdminUser]);

  // Refetch inicial y cuando cambie el usuario/rol
  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  // Refetch cuando la pestaña recupera foco (p. ej., al volver de Wompi)
  useEffect(() => {
    const onFocus = () => {
      if (!isAdminUser && user?.id) {
        fetchWalletData();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchWalletData, isAdminUser, user?.id]);

  // Polling corto para capturar la acreditación poco después de abrir el link de pago
  const startShortPolling = useCallback(() => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    pollEndTimeRef.current = Date.now() + POLL_TOTAL_MS;

    const tick = async () => {
      await fetchWalletData();

      const timeLeft = pollEndTimeRef.current - Date.now();
      if (timeLeft <= 0) {
        clearPolling();
        return;
      }
      pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    // primer intento inmediato
    pollTimerRef.current = setTimeout(tick, 0);
  }, [fetchWalletData]);

  useEffect(() => {
    return () => clearPolling(); // cleanup al desmontar
  }, []);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Cuando el modal de recarga finaliza el flujo (creación del link y redirección),
  // arrancamos un polling de corta duración para reflejar el saldo cuando el webhook/confirm llegue.
  const handleCreditAdded = () => {
    // Refetch inmediato y polling corto
    fetchWalletData();
    startShortPolling();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress
            size={60}
            sx={{ color: '#002642' }}
          />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <Alert
          severity="error"
          sx={{
            borderRadius: 3,
            '& .MuiAlert-message': {
              fontSize: '1.1rem'
            }
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (isAdminUser) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
        <Fade in={true} timeout={600}>
          <Box>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: { xs: 3, sm: 4 }
            }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AdminIcon sx={{ color: '#FFFFFF', fontSize: '2rem' }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: '#002642',
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                    mb: 0.5
                  }}
                >
                  Panel de Administrador
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#75787B',
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                >
                  Gestión de Billetera Digital
                </Typography>
              </Box>
            </Box>

            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#002642',
                    mb: 2,
                    fontWeight: 600
                  }}
                >
                  Información para Administradores
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#75787B',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                >
                  Esta sección está diseñada para la gestión de billeteras de los residentes.
                  Como administrador, puedes gestionar todas las transacciones y operaciones
                  financieras desde la sección de <strong>"Gestión Financiera"</strong> en el menú principal.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      <Fade in={true} timeout={600}>
        <Box>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2.5, sm: 4 }
          }}>
            <Box
              sx={{
                p: { xs: 1.25, sm: 1.5 },
                borderRadius: { xs: 2, sm: 3 },
                background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <WalletIcon sx={{
                color: '#FFFFFF',
                fontSize: { xs: '1.75rem', sm: '2rem' }
              }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#002642',
                  fontSize: { xs: '1.375rem', sm: '2rem', md: '2.5rem' },
                  mb: { xs: 0.25, sm: 0.5 },
                  lineHeight: { xs: 1.2, sm: 1.1 }
                }}
              >
                Mi Billetera
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#75787B',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  lineHeight: 1.3
                }}
              >
                Gestiona tu saldo y revisa tu historial
              </Typography>
            </Box>
          </Box>

          {/* Código de migración a ActionEV (sólo si el usuario tiene uno) */}
          <MigrationCodeCard />

          {/* Balance Cards */}
          {wallet && (
            <SaldoInsuficienteAlert
              balance={wallet.balance}
              showRechargeButton={false}
              sx={{ mb: { xs: 2.5, sm: 3 } }}
            />
          )}
          {wallet && <BilleteraHeader balance={wallet.balance} credit_limit={wallet.credit_limit} />}

          {/* Add Credit Button */}
          <Box sx={{ mb: { xs: 2.5, sm: 4 } }}>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              onClick={handleOpenModal}
              disabled={!wallet}
              sx={{
                backgroundColor: '#002642',
                color: '#FFFFFF',
                py: { xs: 1.25, sm: 2 },
                px: { xs: 2.5, sm: 4 },
                borderRadius: { xs: 2, sm: 3 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 600,
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 200 },
                boxShadow: { xs: 2, sm: 3 },
                '&:hover': {
                  backgroundColor: '#00577A',
                  boxShadow: { xs: 3, sm: 6 },
                  transform: { xs: 'none', sm: 'translateY(-2px)' }
                },
                '&:disabled': {
                  backgroundColor: '#BDBDBD',
                  color: '#FFFFFF'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Añadir Saldo
            </Button>
          </Box>

          {/* Transactions List */}
          <TransactionList transactions={transactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)} />

          <TablePagination
            component="div"
            count={transactions.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Transacciones por página:"
          />

          {/* Modal */}
          {wallet && (
            <AddCreditModal
              isOpen={isModalOpen}
              userId={user.id}
              onClose={handleCloseModal}
              onCreditAdded={handleCreditAdded}
            />
          )}
        </Box>
      </Fade>
    </Container>
  );
};

const MiBilleteraPage = () => {
  return <BilleteraContent />;
};

export default MiBilleteraPage;
